<template>
  <v-container fluid class="py-2">
    <v-row justify="center">
      <v-col cols="12" xl="8" class="pt-0" style="max-width: 980px;">
        <div class="my-preferences-subtitle">
          {{ $t('my-preferences-subtitle') }}
        </div>

        <v-card class="mt-2" outlined>
          <v-card-title class="text-subtitle-1 font-weight-medium py-2">
            {{ $t('my-preferences-default-home-title') }}
          </v-card-title>
          <v-card-text class="py-2">
            <div class="text-caption mb-2">{{ $t('dashboard') }}</div>
            <v-radio-group v-model="homeMain" class="mt-0" hide-details>
              <v-radio v-if="canShowAnalyticsHomeOption" :label="$t('my-preferences-home-analytics')" value="analytics" />
              <v-radio :label="$t('my-preferences-home-assets')" value="assets" />
            </v-radio-group>

            <div v-if="homeMain === 'assets'" class="mt-2">
              <div class="pl-4">
                <div class="text-caption mb-2">{{ $t('my-preferences-default-assets-view-title') }}</div>
                <v-radio-group v-model="assetsSubview" class="mt-0" hide-details>
                  <v-radio :label="$t('my-preferences-assets-list')" value="list" />
                  <v-radio :label="$t('my-preferences-assets-map')" value="map" />
                  <v-radio :label="$t('my-preferences-assets-summary')" value="summary" />
                </v-radio-group>
              </div>
            </div>

            <div class="text-caption mt-3">
              {{ $t('my-preferences-default-home-helper') }}
            </div>
          </v-card-text>
          <v-card-actions class="py-2">
            <v-spacer />
            <v-btn color="secondary" depressed height="32" class="px-6" @click="onSaveHomePreference">
              {{ $t('save') }}
            </v-btn>
          </v-card-actions>
        </v-card>

        <v-card class="mt-3" outlined>
          <v-card-title class="text-subtitle-1 font-weight-medium py-2">
            {{ $t('my-preferences-default-map-view-title') }}
          </v-card-title>
          <v-card-text class="py-2">
            <div class="d-flex align-center">
              <v-chip
                small
                class="mr-2"
                :color="defaultMapView ? 'success' : 'grey'"
                text-color="white"
              >
                {{ defaultMapView ? $t('my-preferences-saved') : $t('my-preferences-not-saved') }}
              </v-chip>
              <div class="text-caption">
                {{ $t('my-preferences-default-map-view-helper') }}
              </div>
            </div>

            <div v-if="defaultMapView" class="text-caption mt-3">
              {{ $t('my-preferences-default-map-view-details') }}
            </div>
          </v-card-text>
          <v-card-actions class="py-2">
            <v-btn text color="secondary" @click="goToMapToSetDefault">
              {{ $t('my-preferences-open-map') }}
            </v-btn>
            <v-spacer />
            <v-btn
              depressed
              height="32"
              class="px-6"
              color="secondary"
              :disabled="!defaultMapView"
              @click="onClearDefaultMapView"
            >
              {{ $t('my-preferences-clear-default-map-view') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'MyPreferencesPage',
  data() {
    return {
      homeMain: 'analytics',
      assetsSubview: 'list',
    };
  },
  computed: {
    ...mapGetters('preferences', ['defaultMapView', 'defaultHomePage', 'loaded']),
    ...mapGetters('organization', ['currentOrganization']),
    ...mapGetters('site', ['currentSite']),
    canShowAnalyticsHomeOption() {
      return (
        this.currentOrganization?.isOrgCustomReportsEnable === true &&
        this.currentSite?.isCustomReportsEnable === true
      );
    },
    homeValue() {
      if (this.homeMain === 'analytics') return 'analytics';
      if (this.homeMain === 'assets') return `assets/${this.assetsSubview}`;
      return 'analytics';
    },
  },
  methods: {
    ...mapActions('preferences', [
      'clearDefaultMapView',
      'loadMePreferences',
      'saveDefaultHomePage',
    ]),
    applyDefaultHomePage(value) {
      const v = value;
      if (!v) return;

      if (v === 'analytics') {
        if (this.canShowAnalyticsHomeOption) {
          this.homeMain = 'analytics';
          return;
        }
        this.homeMain = 'assets';
        this.assetsSubview = 'list';
        return;
      }

      if (v === 'assets' || v === 'assets/list') {
        this.homeMain = 'assets';
        this.assetsSubview = 'list';
        return;
      }

      if (v === 'assets/map') {
        this.homeMain = 'assets';
        this.assetsSubview = 'map';
        return;
      }

      if (v === 'assets/summary') {
        this.homeMain = 'assets';
        this.assetsSubview = 'summary';
      }
    },
    async onSaveHomePreference() {
      try {
        await this.saveDefaultHomePage(this.homeValue);
        this.$toasted.show(this.$t('saved'), {
          position: 'bottom-right',
          className: ['toast-success'],
          duration: 1500,
        });
      } catch (e) {
        this.$toasted.show(this.$t('something-went-wrong'), {
          position: 'bottom-right',
          className: ['toast-error'],
          duration: 5000,
        });
      }
    },
    async onClearDefaultMapView() {
      try {
        await this.clearDefaultMapView();
        this.$toasted.show(this.$t('saved'), {
          position: 'bottom-right',
          className: ['toast-success'],
          duration: 1500,
        });
      } catch (e) {
        this.$toasted.show(this.$t('something-went-wrong'), {
          position: 'bottom-right',
          className: ['toast-error'],
          duration: 5000,
        });
      }
    },
    goToMapToSetDefault() {
      this.$router.push('/assets');
    },
  },
  created() {
    if (!this.loaded) {
      this.loadMePreferences()
        .then(() => {
          this.applyDefaultHomePage(this.defaultHomePage);
        })
        .catch(() => {
          // ignore; page can render with empty defaults
        });
    } else {
      this.applyDefaultHomePage(this.defaultHomePage);
    }
  },
};
</script>

<style scoped>
.my-preferences-subtitle {
  font-size: 14px;
  font-weight: 400;
  color: #5f6368;
  margin-top: 16px;
  margin-bottom: 16px;
}
</style>
